/**
 * GW Featured Collection
 *
 * Uses Splide.js with Grid extension for:
 * - Mobile: 2x2 grid layout per page
 * - Desktop: Horizontal carousel with 4 visible cards
 *
 * Uses MutationObserver to detect when tab panels become visible,
 * then initializes/reinitializes the slider.
 */

class GwFeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.sliders = new Map();
    this.observer = null;
    this.pendingInit = new Map(); // Debounce map for carousel initialization
  }

  connectedCallback() {
    this.initVisibleSliders();
    this.observeTabPanels();
  }

  disconnectedCallback() {
    this.destroySliders();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initVisibleSliders() {
    if (typeof Splide === 'undefined') {
      document.addEventListener('DOMContentLoaded', () => this.initVisibleSliders());
      return;
    }

    // Only initialize carousels that are visible (not in hidden tab panels)
    const carousels = this.querySelectorAll('.gw-featured-collection__carousel');
    carousels.forEach((carousel) => {
      const tabpanel = carousel.closest('[role="tabpanel"]');
      if (!tabpanel || !tabpanel.hasAttribute('hidden')) {
        this.initSingleSlider(carousel);
      }
    });
  }

  initSingleSlider(carousel, collectionUrl) {
    // Destroy existing slider first if present
    if (this.sliders.has(carousel)) {
      console.log('DESTROYING SLIDER', carousel);
      this.sliders.get(carousel).destroy();
      this.sliders.delete(carousel);
    }

    console.log('INITIALIZING SLIDER', carousel, collectionUrl);

    const splide = new Splide(carousel, {
      type: 'slide',
      autoWidth: true,
      padding: { left: 'var(--gw-page-width-offset)', right: 'var(--gw-page-width-offset)' },
      pagination: true,
      arrows: false,
      perPage: 4,
      perMove: 4,
      // perMove: 1,
      gap: '8px',
      grid: false,
      // focus: 0,
      // omitEnd: true,
      classes: {
        pagination: 'splide__pagination gw-featured-collection__dots',
        page: 'splide__pagination__page gw-featured-collection__dot',
      },
      breakpoints: {
        750: {
          autoWidth: false,
          perPage: 1,
          perMove: 1,
          gap: '16px',
          focus: undefined,
          omitEnd: false,
          grid: {
            rows: 2,
            cols: 2,
            gap: {
              row: '8px',
              col: '8px',
            },
          },
        },
      },
    });

    if (collectionUrl) {
      splide.on('mounted', () => {
        const shopBtn = this.querySelector('[data-collection-link]');
        if (shopBtn) {
          shopBtn.href = collectionUrl;
        }
      });
    }

    if (window.splide?.Extensions?.Grid) {
      splide.mount({ Grid: window.splide.Extensions.Grid });
    } else {
      splide.mount();
    }

    this.sliders.set(carousel, splide);
    this.setupWheelNavigation(carousel, splide);
  }

  setupWheelNavigation(carousel, splide) {
    const track = carousel.querySelector('.splide__track');
    const list = carousel.querySelector('.splide__list');
    if (!track || !list) return;

    let snapTimeout = null;

    track.addEventListener(
      'wheel',
      (e) => {
        // Only respond to horizontal scroll (trackpad horizontal swipe)
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
        if (Math.abs(e.deltaX) < 2) return;

        const { Move, Controller } = splide.Components;
        const position = Move.getPosition();
        const endPosition = Move.toPosition(Controller.getEnd());

        // Allow natural page scroll at carousel boundaries
        if (e.deltaX > 0 && position <= endPosition) return;
        if (e.deltaX < 0 && position >= 0) return;

        e.preventDefault();

        // Disable CSS transitions for direct 1:1 tracking with the finger
        list.style.transition = 'none';

        // Move the track directly by the scroll delta, clamped to boundaries
        const newPosition = Math.max(
          endPosition,
          Math.min(0, position - e.deltaX)
        );
        Move.translate(newPosition);

        // Snap to nearest slide when scrolling stops
        if (snapTimeout) clearTimeout(snapTimeout);
        snapTimeout = setTimeout(() => {
          // Re-enable transitions so the snap animates smoothly
          list.style.transition = '';

          const currentPos = Move.getPosition();
          const endIdx = Controller.getEnd();
          let closestIndex = 0;
          let closestDist = Infinity;

          for (let i = 0; i <= endIdx; i++) {
            const dist = Math.abs(currentPos - Move.toPosition(i));
            if (dist < closestDist) {
              closestDist = dist;
              closestIndex = i;
            }
          }

          splide.go(closestIndex);
        }, 150);
      },
      { passive: false }
    );
  }

  observeTabPanels() {
    const panels = this.querySelectorAll('[role="tabpanel"]');
    if (panels.length === 0) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          const panel = mutation.target;
          const collectionUrl = panel.firstElementChild.dataset.collectionUrl;
          const carousel = panel.querySelector('.gw-featured-collection__carousel');

          if (!carousel) return;

          // Cancel any pending initialization for this carousel
          if (this.pendingInit.has(carousel)) {
            cancelAnimationFrame(this.pendingInit.get(carousel));
            this.pendingInit.delete(carousel);
          }

          // Only initialize if panel is now visible
          if (!panel.hasAttribute('hidden')) {
            const frameId = requestAnimationFrame(() => {
              this.pendingInit.delete(carousel);
              this.initSingleSlider(carousel, collectionUrl);
            });
            this.pendingInit.set(carousel, frameId);
          }
        }
      });
    });

    panels.forEach((panel) => {
      this.observer.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    });
  }

  destroySliders() {
    this.sliders.forEach((slider) => {
      if (slider && slider.destroy) {
        slider.destroy();
      }
    });
    this.sliders.clear();
  }
}

customElements.define('gw-featured-collection', GwFeaturedCollection);

// Re-initialize for Shopify section rendering in the theme editor
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const component = section.querySelector('gw-featured-collection');
  if (component) {
    component.initSliders();
  }
});
